import { createContext, useContext, useReducer, useEffect, useRef } from 'react';

const CartContext = createContext();

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existingItem = state.items.find(item => item.product.id === action.payload.id);
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            item.product.id === action.payload.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          ),
        };
      }
      return {
        ...state,
        items: [...state.items, { product: action.payload, quantity: 1, isMarketplace: action.payload.is_marketplace || false }],
      };
    }
    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter(item => item.product.id !== action.payload),
      };
    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item =>
          item.product.id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        ),
      };
    case 'CLEAR_CART':
      return { ...state, items: [] };
    case 'LOAD_CART':
      return { ...state, items: action.payload };
    default:
      return state;
  }
};

// Initialize state from localStorage
const getInitialState = () => {
  if (typeof window !== 'undefined') {
    const savedCart = localStorage.getItem('onlyskullz-cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        if (Array.isArray(parsedCart)) {
          return { items: parsedCart };
        }
      } catch (e) {
        console.error('Error loading cart:', e);
      }
    }
  }
  return { items: [] };
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, null, getInitialState);
  const isInitialMount = useRef(true);

  // Save cart to localStorage on change (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    localStorage.setItem('onlyskullz-cart', JSON.stringify(state.items));
  }, [state.items]);

  // Check if cart contains marketplace items
  const hasMarketplaceItems = () => {
    return state.items.some(item => item.isMarketplace || item.product?.is_marketplace);
  };

  // Check if cart contains shop items
  const hasShopItems = () => {
    return state.items.some(item => !item.isMarketplace && !item.product?.is_marketplace);
  };

  // Get cart type: 'empty', 'shop', or 'marketplace'
  const getCartType = () => {
    if (state.items.length === 0) return 'empty';
    if (hasMarketplaceItems()) return 'marketplace';
    return 'shop';
  };

  // Add item with mixed cart blocking
  // Returns: { success: boolean, blocked: boolean, cartType: string }
  const addItem = (product) => {
    const isMarketplace = product.is_marketplace || false;
    const cartType = getCartType();
    
    // Check for mixed cart conflict
    if (cartType !== 'empty') {
      const cartIsMarketplace = cartType === 'marketplace';
      if (isMarketplace !== cartIsMarketplace) {
        // Blocked - would create mixed cart
        return { 
          success: false, 
          blocked: true, 
          cartType,
          attemptedType: isMarketplace ? 'marketplace' : 'shop'
        };
      }
    }
    
    dispatch({ type: 'ADD_ITEM', payload: product });
    return { success: true, blocked: false };
  };

  const removeItem = (productId) => {
    dispatch({ type: 'REMOVE_ITEM', payload: productId });
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeItem(productId);
    } else {
      dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
    }
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  // Clear cart and add new item (for switching cart type)
  const clearAndAdd = (product) => {
    dispatch({ type: 'CLEAR_CART' });
    // Use setTimeout to ensure clear happens first
    setTimeout(() => {
      dispatch({ type: 'ADD_ITEM', payload: product });
    }, 0);
    return { success: true, blocked: false };
  };

  const getTotal = () => {
    return state.items.reduce((total, item) => total + item.product.price * item.quantity, 0);
  };

  const getItemCount = () => {
    return state.items.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        items: state.items,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        clearAndAdd,
        getTotal,
        getItemCount,
        getCartType,
        hasMarketplaceItems,
        hasShopItems,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
